/**
 * Create account's route: /signup, with `?next=<path>` when it was opened from a sign-in page that must come back to
 * where it was (desktop sign-in's /desktop-auth, an invite, a file link). Only same-site paths are followed.
 */
export const SIGNUP_PATH = "/signup";

/** A same-site path to come back to ("/…", never "//host" or a URL), else undefined. */
export function safeNext(raw: string | null | undefined): string | undefined {
  if (raw === null || raw === undefined || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return undefined;
  return raw;
}

/** /signup for a sign-in page at `returnTo` (the page's path and query). */
export function signupHref(returnTo: string): string {
  const next = safeNext(returnTo);
  return next === undefined || next === "/" || next.startsWith(SIGNUP_PATH) ? SIGNUP_PATH : `${SIGNUP_PATH}?next=${encodeURIComponent(next)}`;
}

/** Where Create account goes back to (sign-in, or after "Secure your account"). */
export function signupNext(search: string): string {
  return safeNext(new URLSearchParams(search).get("next")) ?? "/";
}

/**
 * "New to Bundle? Create an account" on a sign-in page at `returnTo`. Desktop signs in through the system browser,
 * so Create account happens there too: this opens it, and the browser's sign-in page links to /signup.
 */
export function openSignup(session: { state: { browser?: unknown }; startBrowserSignIn(): Promise<void>; openSignup(): void }, returnTo: string): void {
  if (session.state.browser !== undefined) {
    void session.startBrowserSignIn();
    return;
  }
  session.openSignup();
  goTo(signupHref(returnTo));
}

/** Client-side navigation: the Gate re-reads the address on popstate. */
export function goTo(path: string, replace = false): void {
  if (replace) history.replaceState(null, "", path);
  else history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
